import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import {
  INITIALIZE_FCM,
  INITIALIZE_FCM_FAILURE,
  INITIALIZE_FCM_SUCCESS,
  REQUEST_FCM_PERMISSION,
  REQUEST_FCM_PERMISSION_FAILURE,
  REQUEST_FCM_PERMISSION_SUCCESS,
  GET_FCM_TOKEN,
  GET_FCM_TOKEN_FAILURE,
  GET_FCM_TOKEN_SUCCESS,
  ACTION_SAVE_FCM_TOKEN,
  SAVE_FCM_TOKEN_SUCCESS,
  SAVE_FCM_TOKEN_FAILURE,
  ACTION_DELETE_FCM_TOKEN,
  DELETE_FCM_TOKEN_SUCCESS,
  saveFCMTokenAPI,
  deleteFCMTokenAPI,
  DELETE_FCM_TOKEN_FAILURE,
  FCM_MESSAGE_RECEIVED_FOREGROUND,
} from '.';
import {
  initializeFCM as initializeFCMUtil,
  requestNotificationPermission,
  getFCMToken as getFCMTokenUtil,
  setupForegroundMessageHandler,
} from '../../../utils/fcm';
import { getErrorMessage } from '../../../utils/error';
import { refreshNotifications } from '../userNotifications';
import { showToastAlert } from '../app';

function* initializeFCM() {
  yield takeLatest([INITIALIZE_FCM], function* () {
    try {
      // Check current permission status
      const currentPermission = typeof window !== 'undefined' ? Notification.permission : 'default';
      console.log('[FCM] Current notification permission:', currentPermission);

      // If permission is 'default', request it first
      if (currentPermission === 'default') {
        console.log('[FCM] Requesting notification permission...');
        const hasPermission = yield call(requestNotificationPermission);

        yield put({
          type: REQUEST_FCM_PERMISSION_SUCCESS,
          payload: hasPermission,
        });

        if (!hasPermission) {
          console.warn('[FCM] Notification permission not granted by user');
          yield put({
            type: INITIALIZE_FCM_SUCCESS,
            payload: {
              token: null,
              hasPermission: false,
            },
          });
          return;
        }
      }

      // Initialize FCM (register service worker and get token)
      // Firebase automatically caches the token, so getToken() will return cached token if available
      // VAPID key is automatically read from env
      const token = yield call(initializeFCMUtil);

      if (token) {
        // Set up foreground message handler
        // The callback will dispatch FCM_MESSAGE_RECEIVED_FOREGROUND action
        setupForegroundMessageHandler((payload) => {
          console.log('Foreground message received:', payload);
          // Dispatch action to handle in saga
          if (typeof window !== 'undefined' && window.__REDUX_STORE__) {
            window.__REDUX_STORE__.dispatch({
              type: FCM_MESSAGE_RECEIVED_FOREGROUND,
              payload,
            });
          }
        });

        yield put({
          type: INITIALIZE_FCM_SUCCESS,
          payload: {
            token,
            hasPermission: Notification.permission === 'granted',
          },
        });

        // Automatically save token to backend if user is authenticated
        yield put({
          type: ACTION_SAVE_FCM_TOKEN,
          payload: { token },
        });
      } else {
        yield put({
          type: INITIALIZE_FCM_SUCCESS,
          payload: {
            token: null,
            hasPermission: Notification.permission === 'granted',
          },
        });
      }
    } catch (error) {
      console.error('[FCM] Error initializing FCM:', error);
      yield put({
        type: INITIALIZE_FCM_FAILURE,
        payload: getErrorMessage(error),
      });
    }
  });
}

function* requestFCMPermission() {
  yield takeLatest([REQUEST_FCM_PERMISSION], function* () {
    try {
      const hasPermission = yield call(requestNotificationPermission);

      yield put({
        type: REQUEST_FCM_PERMISSION_SUCCESS,
        payload: hasPermission,
      });

      // If permission granted, try to get token
      if (hasPermission) {
        yield put({
          type: GET_FCM_TOKEN,
          payload: {},
        });
      }
    } catch (error) {
      console.error('Error requesting FCM permission:', error);
      yield put({
        type: REQUEST_FCM_PERMISSION_FAILURE,
        payload: getErrorMessage(error),
      });
    }
  });
}

function* getFCMToken() {
  yield takeLatest([GET_FCM_TOKEN], function* () {
    try {
      // VAPID key is automatically read from env
      // Firebase automatically caches the token, so getToken() will return cached token if available
      const token = yield call(getFCMTokenUtil);

      yield put({
        type: GET_FCM_TOKEN_SUCCESS,
        payload: token,
      });

      // Automatically save token to backend if token is available
      if (token) {
        yield put({
          type: ACTION_SAVE_FCM_TOKEN,
          payload: { token },
        });
      }
    } catch (error) {
      console.error('[FCM] Error getting FCM token:', error);
      yield put({
        type: GET_FCM_TOKEN_FAILURE,
        payload: getErrorMessage(error),
      });
    }
  });
}

function* saveFCMToken() {
  yield takeLatest([ACTION_SAVE_FCM_TOKEN], function* (action) {
    // Get token from action.payload or from state
    let token = null;

    if (action.payload?.token) {
      token = action.payload.token;
    } else {
      // Fallback: get token from state
      const state = yield select();
      token = state.fcm?.fcmToken;
    }

    if (!token) {
      console.warn('FCM token not found in action or state, skipping save');
      return;
    }

    // Dispatch the thunk action - redux-thunk will handle it
    // API will automatically generate/get deviceId and include it in the request
    yield put(saveFCMTokenAPI(token));
  });

  yield takeLatest([SAVE_FCM_TOKEN_SUCCESS], function* () {
    console.log('FCM token saved successfully to backend');
    yield;
  });

  yield takeLatest([SAVE_FCM_TOKEN_FAILURE], function* (action) {
    const messageError = action?.payload
      ? getErrorMessage(action?.payload)
      : 'Failed to save FCM token!';
    console.error('[FCM] Error saving FCM token:', messageError);
    yield;
  });
}

function* deleteFCMToken() {
  yield takeLatest([ACTION_DELETE_FCM_TOKEN], function* () {
    yield put(deleteFCMTokenAPI());
  });

  yield takeLatest([DELETE_FCM_TOKEN_SUCCESS], function* () {
    console.log('FCM token deleted successfully from backend');
    yield; // Required for generator function
  });

  yield takeLatest([DELETE_FCM_TOKEN_FAILURE], function* (action) {
    const messageError = action?.payload
      ? getErrorMessage(action?.payload)
      : 'Failed to delete FCM token!';
    console.error('[FCM] Error deleting FCM token:', messageError);
    yield; // Required for generator function
  });
}

function* handleForegroundMessage() {
  yield takeLatest([FCM_MESSAGE_RECEIVED_FOREGROUND], function* (action) {
    const payload = action.payload;

    // Check if this is a badge update message (not a new notification)
    const messageType = payload.data?.type;
    const isBadgeUpdate = messageType === 'badge';

    // Always refresh notifications to update badge count
    yield put(refreshNotifications());

    // Only show toast if it's NOT a badge update message
    if (!isBadgeUpdate) {
      // Show toast notification for actual new notifications
      const notification = payload.notification || payload.data?.notification || {};
      const body =
        notification.body ||
        payload.data?.body ||
        payload.data?.message ||
        'You have a new notification';

      yield put(showToastAlert(body, 'info'));
    }
  });
}

export default function* fcmSaga() {
  yield all([
    fork(initializeFCM),
    fork(requestFCMPermission),
    fork(getFCMToken),
    fork(saveFCMToken),
    fork(deleteFCMToken),
    fork(handleForegroundMessage),
  ]);
}
